// Sample inbox items for the notification hub, one or more per NotificationType
// from the shared enum. Copy + deep-link data mirror the catalogue and the
// backend deepLink whitelist (screen ∈ animal | pawmatch | feed). Timestamps are
// generated relative to `now` so timeframe filters always have fresh data.

import type { NotificationItem, NotificationType } from "./model.js";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

interface MockSeed {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  deepLinkData: Record<string, string>;
  /** Age of the item, in ms before `now`. */
  agoMs: number;
  read: boolean;
  coalesceCount?: number;
}

// Cuid-like ids (match the backend's /^[a-z0-9]{20,32}$/ deep-link whitelist).
const DOG_ID = "cm5wishadopteddog0000001";
const CAT_ID = "cm5wishadoptedcat0000002";

const SEEDS: MockSeed[] = [
  {
    id: "mock-wishlisted-dog",
    type: "WISHLISTED_ANIMAL_ADOPTED",
    title: "Bolinha encontrou uma familia",
    body: "Um animal da tua lista foi adotado. Ha muitos outros a espera de uma familia como a tua.",
    deepLinkData: {
      type: "WISHLISTED_ANIMAL_ADOPTED",
      screen: "animal",
      animalId: DOG_ID,
      animalType: "cao",
    },
    agoMs: 8 * MINUTE,
    read: false,
  },
  {
    id: "mock-new-matches",
    type: "NEW_MATCHES_PAWMATCH",
    title: "Novos candidatos no PawMatch",
    body: "Encontramos 5 novos animais que combinam contigo.",
    deepLinkData: { type: "NEW_MATCHES_PAWMATCH", screen: "pawmatch" },
    agoMs: 3 * HOUR,
    read: false,
    coalesceCount: 5,
  },
  {
    id: "mock-daily-swipes",
    type: "PAWMATCH_DAILY_SWIPES_READY",
    title: "Os teus swipes de hoje estao prontos",
    body: "Volta ao PawMatch para conhecer mais animais para adotar.",
    deepLinkData: { type: "PAWMATCH_DAILY_SWIPES_READY", screen: "pawmatch" },
    agoMs: 6 * HOUR,
    read: true,
  },
  {
    id: "mock-first-like",
    type: "FIRST_LIKE_NUDGE",
    title: "Encontra o teu primeiro favorito",
    body: "Guarda os animais de que gostas para os acompanhares de perto.",
    deepLinkData: { type: "FIRST_LIKE_NUDGE", screen: "feed" },
    agoMs: 2 * DAY,
    read: false,
  },
  {
    id: "mock-wishlisted-cat",
    type: "WISHLISTED_ANIMAL_ADOPTED",
    title: "Mia encontrou uma familia",
    body: "Um animal da tua lista foi adotado. Ha muitos outros a espera de uma familia como a tua.",
    deepLinkData: {
      type: "WISHLISTED_ANIMAL_ADOPTED",
      screen: "animal",
      animalId: CAT_ID,
      animalType: "gato",
    },
    agoMs: 4 * DAY,
    read: true,
  },
  {
    id: "mock-weekly-digest",
    type: "WEEKLY_DIGEST",
    title: "Esta semana perto de ti",
    body: "8 novos caes e gatos a espera de uma familia.",
    deepLinkData: { type: "WEEKLY_DIGEST", screen: "feed" },
    agoMs: 6 * DAY,
    read: true,
    coalesceCount: 8,
  },
  {
    id: "mock-comeback-pawmatch",
    type: "COMEBACK_PAWMATCH",
    title: "Sentimos a tua falta no PawMatch",
    body: "Volta ao PawMatch e continua a descobrir animais para adotar.",
    deepLinkData: { type: "COMEBACK_PAWMATCH", screen: "pawmatch" },
    agoMs: 20 * DAY,
    read: true,
  },
  {
    id: "mock-new-matches-old",
    type: "NEW_MATCHES_PAWMATCH",
    title: "Novos candidatos no PawMatch",
    body: "Encontramos 3 novos animais que combinam contigo.",
    deepLinkData: { type: "NEW_MATCHES_PAWMATCH", screen: "pawmatch" },
    agoMs: 40 * DAY,
    read: true,
    coalesceCount: 3,
  },
];

/** Build the mock inbox, newest first, dated relative to `now`. */
export function buildMockNotifications(now: Date = new Date()): NotificationItem[] {
  return SEEDS.map((seed) => {
    const createdAt = new Date(now.getTime() - seed.agoMs).toISOString();
    return {
      id: seed.id,
      type: seed.type,
      title: seed.title,
      body: seed.body,
      deepLinkData: seed.deepLinkData,
      readAt: seed.read ? createdAt : null,
      createdAt,
      coalesceCount: seed.coalesceCount ?? 1,
    };
  }).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}
